import { Anchor, Badge, Button, Group, Paper, Stack, Text } from "@mantine/core"
import { useEffect, useState } from "react"
import { VisibilityBadge } from "atlas_forecasting_app/components/common/visibility_badge"
import { formatDateTime } from "atlas_forecasting_app/lib/format"
import { RouterLink } from "atlas_forecasting_app/lib/router"
import { ForecastDisplay } from "atlas_forecasting_app/components/forecast/forecast_display"
import { ForecastHistoryChart } from "atlas_forecasting_app/components/forecast/forecast_history_chart"

export function QuestionForecastCard({ item, variant = "feed" }) {
  const question = item?.question
  const latest = item?.latest_forecast
  const count = Number(item?.forecast_count) || 0
  const graphMode = item?.graph_mode
  const recent = Array.isArray(item?.recent_forecasts) && item.recent_forecasts.length > 0 ? item.recent_forecasts : latest ? [latest] : []

  const [selectedId, setSelectedId] = useState(null)
  const selectedForecast = selectedId ? recent.find((f) => f.id === selectedId) : null

  useEffect(() => {
    if (!selectedId) return
    if (!recent.some((f) => f.id === selectedId)) setSelectedId(null)
  }, [recent, selectedId])

  if (!question) return null

  const forecastCount = Math.max(count, recent.length)
  const historyAvailable = !!latest && forecastCount >= 2 && recent.length >= 2
  const showHistory = graphMode === "latest" ? false : graphMode === "history" ? historyAvailable : historyAvailable
  const compact = variant !== "feed"
  const showLegend = variant === "feed"
  const multipleChoicePreviewLimit = variant === "feed" ? 7 : undefined

  const createdTime = question.created_at
  const forecastTime = latest && (latest.completed_at || latest.created_at)
  const metaParts = []

  if (variant === "my_questions") {
    if (createdTime) metaParts.push(`Created ${formatDateTime(createdTime)}`)
    if (forecastTime) metaParts.push(`Last forecast ${formatDateTime(forecastTime)}`)
  } else {
    if (forecastTime) metaParts.push(formatDateTime(forecastTime))
  }

  if (forecastCount > 0) metaParts.push(`${forecastCount} forecast${forecastCount === 1 ? "" : "s"}`)
  const meta = metaParts.filter(Boolean).join(" • ")
  const displayVariant = showHistory ? "History view" : "Latest forecast"

  return (
    <Paper withBorder radius="xl" shadow="xs" p="lg" className={`af-question-card af-question-card--${variant}`}>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" gap="md" className="af-question-card__header">
          <Stack gap={8} className="af-question-card__copy">
            <Group gap="xs" className="af-question-card__pills">
              <VisibilityBadge publiclyVisible={question.publicly_visible} visibility={question.visibility} size="md" />
              <Badge variant="light" color="blue" size="md">
                {question.question_type}
              </Badge>
              <Badge variant="light" color="gray" size="md">
                {displayVariant}
              </Badge>
            </Group>
            <Anchor component={RouterLink} to={`/questions/${question.id}`} fw={700} underline="never" className="af-question-card__title">
              {question.title}
            </Anchor>
            <Text size="sm" className="af-question-card__meta">
              {meta}
            </Text>
          </Stack>
          <Badge variant="light" color="blue" size="lg" className="af-question-card__count">
            {forecastCount} forecast{forecastCount === 1 ? "" : "s"}
          </Badge>
        </Group>

        {!latest ? (
          <Text c="dimmed">No forecasts yet.</Text>
        ) : showHistory ? (
          <div className="af-question-card__visual">
            <ForecastHistoryChart
              question={question}
              forecasts={recent}
              selectedForecastId={selectedId}
              onSelectForecastId={setSelectedId}
              compact={compact}
              showLegend={showLegend}
            />
          </div>
        ) : (
          <div className="af-question-card__visual">
            <ForecastDisplay
              question={question}
              forecastData={latest.forecast_data}
              compact={compact}
              multipleChoicePreviewLimit={multipleChoicePreviewLimit}
            />
          </div>
        )}

        {showHistory && selectedForecast ? (
          <Paper withBorder radius="xl" bg="gray.0" p="md" className="af-question-card__selection">
            <Stack gap="md">
              <Group justify="space-between" align="flex-start" gap="md">
                <Stack gap={4}>
                  <Text fw={600}>Selected forecast</Text>
                  <Text size="sm" c="dimmed">
                    {formatDateTime(selectedForecast.completed_at || selectedForecast.created_at)}
                  </Text>
                </Stack>
                <Button type="button" variant="subtle" onClick={() => setSelectedId(null)}>
                  Clear
                </Button>
              </Group>
              <ForecastDisplay
                question={question}
                forecastData={selectedForecast.forecast_data}
                compact={compact}
                multipleChoicePreviewLimit={multipleChoicePreviewLimit}
              />
            </Stack>
          </Paper>
        ) : null}
      </Stack>
    </Paper>
  )
}
